import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';

import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import CustomNavbar from '../components/CustomNavbar';

/* Length of the heartbreak: two beats, a crack, then the two halves fall.
   Mirrored in the CSS below — change it here and there together. */
const BREAK_MS = 1250;

function WishlistPage() {
  const navigate = useNavigate();

  const [wishlistItems, setWishlistItems] = useState([]);

  /* IDs of wishlist cards currently playing the heartbreak animation */
  const [breakingIds, setBreakingIds] = useState(() => new Set());

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) {
        toast.error('Please Login First');

        setLoading(false);

        return;
      }

      const { data } = await axios.get(
        `${API}/wishlist/${userInfo._id}`
      );

      setWishlistItems(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (id) => {
    /* Already breaking — ignore repeat clicks */
    if (breakingIds.has(id)) {
      return;
    }

    /* Break the card's heart first, delete once the pieces have fallen */
    setBreakingIds((prev) => new Set(prev).add(id));

    await new Promise((resolve) => setTimeout(resolve, BREAK_MS));

    try {
      await axios.delete(`${API}/wishlist/${id}`);

      toast.success('Removed From Wishlist', {
        icon: () => <span className="wl-toast-heart">💔</span>,
        className: 'wl-toast-rose'
      });

      await fetchWishlist();
    } catch (error) {
      console.log(error);
    } finally {
      setBreakingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  /*
    The heartbreak overlay: the jagged crack that rips across the card, the
    rose flash at the moment it gives way, and the little hearts that escape
    upward as the halves slide apart.

    The crack path here and the clip-paths on .wl-shard in the CSS trace the
    same zigzag — if one moves, the other has to move with it, or the halves
    stop lining up with the tear.
  */
  const renderBreak = () => {
    const hearts = ['💔', '❤️', '💗', '💘', '🤍'];

    /* Responsive: fewer, shorter-flight hearts on small screens */
    const mobile = window.innerWidth < 576;
    const count = mobile ? 8 : 14;
    const drift = mobile ? 0.65 : 1;

    return (
      <div className="wl-break-layer">
        <span className="wl-break-flash" />

        {/* The tear itself — drawn on with a dash offset so it rips from the
            left across, rather than appearing all at once. */}
        <svg
          className="wl-crack"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            className="wl-crack-glow"
            pathLength="100"
            d="M0 52 L12 46 L24 54 L38 44 L50 53 L63 45 L76 55 L88 47 L100 52"
          />
          <path
            className="wl-crack-line"
            pathLength="100"
            d="M0 52 L12 46 L24 54 L38 44 L50 53 L63 45 L76 55 L88 47 L100 52"
          />
        </svg>

        {/* The heart that gives out, right where the card splits */}
        <span className="wl-break-heart">💔</span>

        {Array.from({ length: count }).map((_, i) => {
          const left = 12 + Math.random() * 76;

          /* Hearts drift up and outward, away from the crack in the middle */
          const dx = (left < 50 ? -1 : 1) * (18 + Math.random() * 62) * drift;
          const dy = -(70 + Math.random() * 110) * drift;
          const rot = (Math.random() - 0.5) * 90;

          return (
            <span
              key={i}
              className="wl-escape-heart"
              style={{
                left: `${left}%`,
                fontSize: `${0.62 + Math.random() * 0.75}rem`,
                animationDelay: `${0.32 + Math.random() * 0.42}s`,
                animationDuration: `${0.85 + Math.random() * 0.4}s`,
                '--dx': `${dx}px`,
                '--dy': `${dy}px`,
                '--rot': `${rot}deg`
              }}
            >
              {hearts[Math.floor(Math.random() * hearts.length)]}
            </span>
          );
        })}
      </div>
    );
  };

  const addToCart = async (item) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) {
        toast.error('Please Login First');

        return;
      }

      await axios.post(
        `${API}/cart`,

        {
          userId: userInfo._id,

          productId: item.productId,

          name: item.name,

          image: item.image,

          price: item.price,

          quantity: 1
        }
      );

      toast.success('Added To Cart');
    } catch (error) {
      console.log(error);
    }
  };

  const buyNow = (item) => {
    navigate(`/product/${item.productId}`);
  };

  /* One card, rendered up to three times while an item is breaking: once as
     the live card, then once inside each falling half. Keeping it in a single
     function is what stops the two halves from drifting out of sync with
     what the user was actually looking at. */
  const renderCard = (item) => {
    const oldPrice = Math.round(item.price * 1.2);

    return (
      <Card className="border-0 shadow-sm h-100 wl-card">
        <div className="wl-img-wrap">
          <button
            type="button"
            className="wl-remove-icon"
            title="Remove from wishlist"
            onClick={() => removeFromWishlist(item._id)}
          >
            <span className="wl-heart-full">❤️</span>
            <span className="wl-heart-broken">💔</span>
          </button>
          <img
            src={item.image}
            alt={item.name}
            className="wl-img"
            onClick={() => buyNow(item)}
          />
        </div>

        <Card.Body className="d-flex flex-column p-3">
          <h6 className="fw-bold wl-name mb-1">{item.name}</h6>

          <div className="mb-2">
            <Badge bg="success" className="rounded-pill me-1">
              In Stock
            </Badge>
            <Badge bg="warning" text="dark" className="rounded-pill">
              🚚 Fast
            </Badge>
          </div>

          <div className="d-flex align-items-baseline gap-2 mb-3">
            <span className="text-success fw-bold wl-price">₹{item.price}</span>
            <small className="text-muted text-decoration-line-through">
              ₹{oldPrice}
            </small>
          </div>

          <div className="d-grid gap-2 mt-auto">
            <Button
              variant="warning"
              size="sm"
              className="fw-bold wl-btn"
              onClick={() => addToCart(item)}
            >
              🛒 Add To Cart
            </Button>
            <div className="d-flex gap-2">
              <Button
                variant="dark"
                size="sm"
                className="flex-fill fw-semibold wl-btn"
                onClick={() => buyNow(item)}
              >
                👀 View
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                className="flex-fill fw-semibold wl-btn"
                onClick={() => removeFromWishlist(item._id)}
              >
                Remove
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      <CustomNavbar />

      <div className="wl-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 wl-hero">
            <span className="wl-hero-blob wl-hero-blob-1" />
            <span className="wl-hero-blob wl-hero-blob-2" />

            <Card.Body className="position-relative p-4 p-md-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h1 className="fw-bold mb-1">❤️ My Wishlist</h1>
                <p className="mb-0 wl-hero-text">
                  Save your favourite products and purchase them anytime.
                </p>
              </div>
              <Badge className="wl-count-badge">
                {wishlistItems.length} Saved
              </Badge>
            </Card.Body>
          </Card>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-warning" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : wishlistItems.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '20px' }}
            >
              <Card.Body>
                <div className="wl-empty-heart">💔</div>
                <h4 className="fw-bold mt-2">Your Wishlist is Empty</h4>
                <p className="text-muted mb-3">
                  Add products to your wishlist to see them here.
                </p>
                <Button
                  variant="dark"
                  className="rounded-pill px-4 fw-semibold"
                  onClick={() => navigate('/products')}
                >
                  🛍 Browse Products
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {wishlistItems.map((item) => {
                const isBreaking = breakingIds.has(item._id);

                return (
                  <Col xl={3} lg={4} md={6} sm={6} xs={12} key={item._id}>
                    <div
                      className={`wl-card-wrap h-100${
                        isBreaking ? ' wl-breaking' : ''
                      }`}
                    >
                      {/* The live card. While the item is breaking it hands
                          over to the two halves stacked on top of it, which
                          together still read as one card until they part. */}
                      <div className="wl-card-face h-100">
                        {renderCard(item)}
                      </div>

                      {isBreaking && (
                        <>
                          <div className="wl-shard is-top" aria-hidden="true">
                            {renderCard(item)}
                          </div>
                          <div className="wl-shard is-bottom" aria-hidden="true">
                            {renderCard(item)}
                          </div>

                          {renderBreak()}
                        </>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{wishlistStyles}</style>
    </>
  );
}

const wishlistStyles = `
  .wl-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }

  /* Header */
  .wl-hero {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #831843 55%, #db2777);
    position: relative;
  }
  .wl-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.5;
    pointer-events: none;
  }
  .wl-hero-blob-1 {
    width: 200px; height: 200px;
    background: #fbbf24;
    top: -80px; left: 18%;
  }
  .wl-hero-blob-2 {
    width: 180px; height: 180px;
    background: #fb7185;
    bottom: -90px; right: 8%;
    opacity: 0.35;
  }
  .wl-hero-text {
    opacity: 0.9;
  }
  .wl-count-badge {
    background: rgba(255,255,255,0.18) !important;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 30px;
    padding: 10px 18px;
    font-size: 1rem;
    font-weight: 600;
  }

  /* Cards */
  .wl-card {
    border-radius: 18px;
    overflow: hidden;
    border: 2px solid transparent !important;
    transition: transform 0.5s ease, box-shadow 0.3s ease, border-color 0.3s ease, opacity 0.5s ease;
  }
  .wl-card:hover {
    transform: translateY(-8px);
    border-color: rgba(219, 39, 119, 0.35) !important;
    box-shadow: 0 24px 44px rgba(219, 39, 119, 0.2) !important;
  }
  .wl-img-wrap {
    position: relative;
    overflow: hidden;
    background: #f8fafc;
  }
  .wl-img {
    height: clamp(170px, 22vw, 210px);
    width: 100%;
    object-fit: contain;
    padding: 12px;
    cursor: pointer;
    transition: transform 0.45s ease;
  }
  .wl-card:hover .wl-img {
    transform: scale(1.08);
  }
  .wl-remove-icon {
    position: absolute;
    top: 10px; right: 10px;
    z-index: 3;
    width: 38px; height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: rgba(255,255,255,0.95);
    font-size: 1.1rem;
    cursor: pointer;
    box-shadow: 0 4px 10px rgba(0,0,0,0.15);
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .wl-remove-icon:hover {
    transform: scale(1.18) rotate(-6deg);
    background: #fff;
  }
  /* Swap the full heart for a breaking one on hover */
  .wl-remove-icon .wl-heart-full,
  .wl-remove-icon .wl-heart-broken {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  .wl-remove-icon .wl-heart-broken { opacity: 0; transform: scale(0.6); }
  .wl-remove-icon:hover .wl-heart-full { opacity: 0; transform: scale(0.6); }
  .wl-remove-icon:hover .wl-heart-broken {
    opacity: 1;
    transform: scale(1);
    animation: wlShake 0.5s ease;
  }
  @keyframes wlShake {
    0%, 100% { transform: rotate(0) scale(1); }
    25% { transform: rotate(-12deg) scale(1.1); }
    75% { transform: rotate(12deg) scale(1.1); }
  }

  /* Animated broken heart on the empty state */
  .wl-empty-heart {
    font-size: 3.6rem;
    display: inline-block;
    animation: wlEmptyBreak 2.6s ease-in-out infinite;
    filter: drop-shadow(0 8px 14px rgba(219,39,119,0.28));
  }
  @keyframes wlEmptyBreak {
    0%, 55%, 100% { transform: rotate(0) scale(1); }
    62% { transform: rotate(-9deg) scale(1.12); }
    70% { transform: rotate(8deg) scale(1.1); }
    78% { transform: rotate(-5deg) scale(1.06); }
    86% { transform: rotate(3deg) scale(1.03); }
  }
  .wl-name {
    font-size: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .wl-price {
    font-size: 1.3rem;
  }
  .wl-btn {
    border-radius: 10px;
    transition: transform 0.2s ease;
  }
  .wl-btn:hover {
    transform: translateY(-2px);
  }

  /* Rose-themed "Removed From Wishlist" toast */
  .wl-toast-rose {
    background: linear-gradient(135deg, #be185d, #f43f5e) !important;
    color: #fff !important;
    border-radius: 14px !important;
    box-shadow: 0 12px 30px rgba(190, 24, 93, 0.4) !important;
  }
  .wl-toast-rose .Toastify__toast-body { color: #fff !important; font-weight: 600; }
  .wl-toast-rose .Toastify__close-button { color: #fff; opacity: 0.9; }
  .wl-toast-rose .Toastify__progress-bar {
    background: linear-gradient(90deg, #fecdd3, #fff) !important;
  }
  .wl-toast-heart {
    display: inline-block;
    font-size: 1.2rem;
    animation: wlToastShake 0.6s ease;
  }
  @keyframes wlToastShake {
    0%, 100% { transform: rotate(0) scale(1); }
    30% { transform: rotate(-14deg) scale(1.15); }
    60% { transform: rotate(12deg) scale(1.15); }
  }

  /* ===== Heartbreak removal =====
     Timed against BREAK_MS in the component, in three beats:
       0.00-0.40s  the card takes two heartbeats and tenses up
       0.30-0.52s  a jagged crack rips down it, rose light spilling out
       0.42-1.25s  the two halves tip apart and fall away under gravity
     Both halves are full-size copies of the card, each clipped to one side
     of the crack. Because they are the same size and share a transform
     origin, any shared transform keeps them glued into one card — they only
     read as two pieces once their transforms diverge. */
  .wl-card-wrap {
    position: relative;
  }
  /* The live card hands over to the halves the moment the break starts */
  .wl-breaking .wl-card-face {
    visibility: hidden;
  }

  .wl-shard {
    position: absolute;
    inset: 0;
    z-index: 5;
    pointer-events: none;
    transform-origin: 50% 50%;
    filter: drop-shadow(0 10px 18px rgba(190,24,93,0.22));
  }
  /* The two halves of the tear — these polygons trace the same zigzag as the
     crack path drawn in renderBreak(), which now runs left to right.
     Top piece: across the top edge, then back along the crack.
     Bottom piece: along the crack, then down around the bottom edge. */
  .wl-shard.is-top {
    clip-path: polygon(0 0, 100% 0, 100% 52%, 88% 47%, 76% 55%, 63% 45%, 50% 53%, 38% 44%, 24% 54%, 12% 46%, 0 52%);
    animation: wlShardTop 1.25s forwards;
  }
  .wl-shard.is-bottom {
    clip-path: polygon(0 52%, 12% 46%, 24% 54%, 38% 44%, 50% 53%, 63% 45%, 76% 55%, 88% 47%, 100% 52%, 100% 100%, 0 100%);
    animation: wlShardBottom 1.25s forwards;
  }
  /* Two beats, a held breath, then the halves are pulled apart sideways.
     The accelerating easing is set on the keyframe where the slide begins,
     so only the escape speeds up — the beats before it stay steady. */
  @keyframes wlShardTop {
    0%   { transform: none; opacity: 1; animation-timing-function: ease-out; }
    8%   { transform: scale(1.04); }
    16%  { transform: scale(0.995); }
    26%  { transform: scale(1.065); }
    34%  { transform: scale(1); }
    /* the crack gives way */
    40%  { transform: translate(0, -3px) rotate(-1.5deg); opacity: 1; animation-timing-function: cubic-bezier(0.42, 0, 0.85, 0.4); }
    100% { transform: translate(-190px, -18px) rotate(-9deg); opacity: 0; }
  }
  @keyframes wlShardBottom {
    0%   { transform: none; opacity: 1; animation-timing-function: ease-out; }
    8%   { transform: scale(1.04); }
    16%  { transform: scale(0.995); }
    26%  { transform: scale(1.065); }
    34%  { transform: scale(1); }
    40%  { transform: translate(0, 3px) rotate(1.5deg); opacity: 1; animation-timing-function: cubic-bezier(0.42, 0, 0.85, 0.4); }
    100% { transform: translate(198px, 20px) rotate(8deg); opacity: 0; }
  }

  .wl-break-layer {
    position: absolute;
    inset: 0;
    z-index: 6;
    pointer-events: none;
    overflow: visible;
  }

  /* The tear, drawn on from the top down */
  .wl-crack {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
  .wl-crack-line, .wl-crack-glow {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 100;
    stroke-dashoffset: 100;
    animation: wlCrackDraw 1.25s ease forwards;
  }
  .wl-crack-line {
    stroke: #fff1f2;
    stroke-width: 1.4;
  }
  .wl-crack-glow {
    stroke: #f43f5e;
    stroke-width: 4;
    filter: blur(2px);
    opacity: 0.9;
  }
  /* Rips across over the first third, then fades as the halves slide out */
  @keyframes wlCrackDraw {
    0%   { stroke-dashoffset: 100; opacity: 0; }
    18%  { opacity: 1; }
    40%  { stroke-dashoffset: 0; opacity: 1; }
    62%  { opacity: 0.45; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  }

  /* Rose light spilling out of the tear */
  .wl-break-flash {
    position: absolute;
    inset: -10%;
    border-radius: 24px;
    background: radial-gradient(ellipse 70% 26% at 50% 50%, rgba(255,255,255,0.85), rgba(244,63,94,0.5) 34%, rgba(190,24,93,0.16) 58%, transparent 76%);
    opacity: 0;
    animation: wlBreakFlash 0.85s ease forwards;
  }
  /* the light spills perpendicular to the tear, so it swells vertically now */
  @keyframes wlBreakFlash {
    0%, 22% { opacity: 0; transform: scaleY(0.6); }
    42%     { opacity: 1; transform: scaleY(1); }
    100%    { opacity: 0; transform: scaleY(1.35); }
  }

  /* The heart that gives out, right on the tear */
  .wl-break-heart {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3.2rem;
    z-index: 7;
    pointer-events: none;
    opacity: 0;
    filter: drop-shadow(0 6px 14px rgba(190,24,93,0.4));
    animation: wlHeartBreak 1.25s ease forwards;
  }
  /* Beats along with the card, then splits open as the halves part */
  @keyframes wlHeartBreak {
    0%   { opacity: 0; transform: scale(0.5); }
    12%  { opacity: 1; transform: scale(1.1); }
    22%  { transform: scale(0.94); }
    34%  { transform: scale(1.24) rotate(-4deg); }
    46%  { opacity: 1; transform: scale(1.05) rotate(5deg); }
    100% { opacity: 0; transform: scale(0.72) rotate(-8deg) translateY(-26px); }
  }

  /* Little hearts escaping as the card lets go */
  .wl-escape-heart {
    position: absolute;
    bottom: 42%;
    line-height: 1;
    opacity: 0;
    animation-name: wlEscape;
    animation-timing-function: cubic-bezier(0.2, 0.65, 0.35, 1);
    animation-fill-mode: forwards;
  }
  @keyframes wlEscape {
    0%   { opacity: 0; transform: translate(0, 0) scale(0.4) rotate(0deg); }
    18%  { opacity: 1; transform: translate(calc(var(--dx) * 0.2), calc(var(--dy) * 0.18)) scale(1) rotate(calc(var(--rot) * 0.2)); }
    70%  { opacity: 0.85; }
    100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.55) rotate(var(--rot)); }
  }

  /* Keep the animation buttery on lower-power devices */
  .wl-shard,
  .wl-escape-heart,
  .wl-break-heart,
  .wl-break-flash {
    will-change: transform, opacity;
  }
  @media (max-width: 575.98px) {
    .wl-break-heart { font-size: 2.5rem; }
    .wl-crack-glow { stroke-width: 3; }
  }

  @media (prefers-reduced-motion: reduce) {
    .wl-shard.is-top, .wl-shard.is-bottom {
      animation: wlShardFade 0.25s ease forwards;
    }
    @keyframes wlShardFade {
      to { opacity: 0; }
    }
    .wl-crack, .wl-break-flash, .wl-escape-heart, .wl-break-heart {
      display: none;
    }
  }
`;

export default WishlistPage;
